import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { useToast } from 'vue-toastification'
import { createRequestID, getRequestID, requestLogContext, REQUEST_ID_HEADER, withRequestID } from '@/utils/requestTracking'

// API Configuration - Use relative path by default to work with any hostname
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'
const API_SERVER_BASE = API_BASE_URL.replace('/api/v1', '')

// Helper function to fix image URLs to point to the backend server
export const fixImageUrl = (url: string, fallbackUrl?: string): string => {
  // If no url provided, return fallback or empty string
  if (!url || url.trim() === '') {
    return fallbackUrl || ''
  }

  // Determine effective server base. In dev, VITE_API_BASE_URL can be relative (e.g. "/api/v1"),
  // which makes API_SERVER_BASE empty. In that case, infer backend as the same host with port 8080
  // when running on :3000, otherwise use current origin.
  let serverBase = API_SERVER_BASE
  if (!serverBase || serverBase.trim() === '') {
    try {
      const { protocol, hostname, port } = window.location
      if (port === '3000') {
        serverBase = `${protocol}//${hostname}:8080`
      } else {
        serverBase = `${protocol}//${hostname}${port ? ':' + port : ''}`
      }
    } catch (_) {
      // Fallback in non-browser contexts
      serverBase = window.location.origin
    }
  }

  // If the URL starts with /static/, prepend the backend server URL
  if (url.startsWith('/static/')) {
    return `${serverBase}${url}`
  }

  // If the URL is already a full URL, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }

  // If the URL starts with /SugarM/, it's a frontend public asset
  if (url.startsWith('/SugarM/')) {
    return url // Return as is, it will be served from frontend public directory
  }

  // For any other relative URLs, prepend the backend server URL
  return `${serverBase}${url.startsWith('/') ? url : '/' + url}`
}

// Request deduplication cache
const pendingRequests = new Map<string, Promise<any>>()

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Helper function to generate request key for deduplication
const getRequestKey = (config: any): string => {
  const { method, url, params, data } = config
  return `${method}:${url}:${JSON.stringify(params || {})}:${JSON.stringify(data || {})}`
}

// Helper function to set auth header
const setAuthHeader = (token: string) => {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`
}

// Helper function to clear auth header
const clearAuthHeader = () => {
  delete api.defaults.headers.common['Authorization']
}

// Request interceptor
api.interceptors.request.use(
  (config) => {
    if (!config.headers[REQUEST_ID_HEADER]) {
      config.headers[REQUEST_ID_HEADER] = createRequestID()
    }

    // Add timestamp to prevent caching for critical requests
    if (config.method === 'get' && (config.url?.includes('/profile') || config.url?.includes('/photos'))) {
      config.params = {
        ...config.params,
        _t: Date.now()
      }
    }

    // Add language header
    const language = localStorage.getItem('language') || 'en'
    config.headers['Accept-Language'] = language

    // Request deduplication for GET requests to profile and photos endpoints
    if (config.method === 'get' && (config.url?.includes('/profile') || config.url?.includes('/photos'))) {
      const requestKey = getRequestKey(config)

      if (pendingRequests.has(requestKey)) {
        console.log(`🔄 Deduplicating request: ${config.method?.toUpperCase()} ${config.url}`)
        // Return the existing promise instead of making a new request
        return pendingRequests.get(requestKey)!.then(() => config)
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Clean up pending request cache for completed requests
    const requestKey = getRequestKey(response.config)
    if (pendingRequests.has(requestKey)) {
      pendingRequests.delete(requestKey)
    }

    ;(response as any).requestId = getRequestID(response)
    return response
  },
  async (error) => {
    // Clean up pending request cache for failed requests
    if (error.config) {
      const requestKey = getRequestKey(error.config)
      if (pendingRequests.has(requestKey)) {
        pendingRequests.delete(requestKey)
      }
    }

    const toast = useToast()
    const originalRequest = error.config
    const requestID = getRequestID(error)
    ;(error as any).requestId = requestID
    if (requestID) {
      console.info('[API] Request failed', {
        method: String(originalRequest?.method || 'request').toUpperCase(),
        ...requestLogContext(error)
      })
    }
    
    // A failed login/register request is an expected credential/validation
    // response, not an expired session. Trying to refresh here creates a
    // second, misleading "session expired" toast on the login page.
    const requestUrl = String(originalRequest?.url || '').split(/[?#]/)[0]
    const isAuthHandshake = /^\/auth\/(login|register|refresh|verify-email|forgot-password|reset-password)(?:\/|$)/.test(requestUrl)

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthHandshake) {
      originalRequest._retry = true
      
      // Try to refresh token
      try {
        const refreshToken = localStorage.getItem('refresh_token')
        if (!refreshToken) {
          throw new Error('No refresh token available')
        }

        // Create a new axios instance to avoid interceptor loop
        const refreshResponse = await axios.post(`${api.defaults.baseURL}/auth/refresh`, {
          refresh_token: refreshToken
        }, {
          headers: { [REQUEST_ID_HEADER]: createRequestID() }
        })

        const newTokenPair = refreshResponse.data.data
        const newAccessToken = newTokenPair.access_token
        const newRefreshToken = newTokenPair.refresh_token

        // Update tokens in localStorage
        localStorage.setItem('auth_token', newAccessToken)
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken)
        }

        // Update authorization header
        api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`

        // Retry original request
        return api(originalRequest)
      } catch (refreshError) {
        // Refresh failed, redirect to login.
        // IMPORTANT: clear ALL three auth storage locations so the header
        // (which reads user from pinia-persist) doesn't show a stale login
        // after the session has expired. See auth.ts persist config (key:
        // 'sugarm_auth') — these keys must be kept in sync, otherwise the
        // header shows the previous user even when no valid token exists,
        // which causes /discover etc. to silently 401-redirect to /auth/login.
        localStorage.removeItem('auth_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('sugarm_auth')
        delete api.defaults.headers.common['Authorization']

        // Show user-friendly message about session expiry
        toast.error(withRequestID('Your session has expired. Please log in again.', getRequestID(refreshError) || requestID))

        // Only redirect if not already on login page
        if (!window.location.pathname.includes('/auth/login')) {
          window.location.href = '/auth/login'
        }

        return Promise.reject(refreshError)
      }
    }
    
    // Handle other common errors
    if (error.response?.status === 403) {
      toast.error(withRequestID('Access denied. You may need to upgrade your membership.', requestID))
    } else if (error.response?.status === 404) {
      toast.error(withRequestID('Resource not found', requestID))
    } else if (error.response?.status === 429) {
      toast.error(withRequestID('Too many requests. Please wait a moment before trying again.', requestID))
    } else if (error.response?.status >= 500) {
      toast.error(withRequestID('Server error. Please try again later.', requestID))
    } else if (error.code === 'NETWORK_ERROR' || !error.response) {
      toast.error(withRequestID('Network error. Please check your internet connection.', requestID))
    }
    
    return Promise.reject(error)
  }
)

// Initialize token from localStorage
const token = localStorage.getItem('auth_token')
if (token) {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`
}

// Helper function to deduplicate API calls
const deduplicatedGet = (url: string, config?: any) => {
  const requestKey = getRequestKey({ method: 'get', url, ...config })

  if (pendingRequests.has(requestKey)) {
    console.log(`🔄 Returning cached promise for: GET ${url}`)
    return pendingRequests.get(requestKey)!
  }

  const promise = api.get(url, config).finally(() => {
    pendingRequests.delete(requestKey)
  })

  pendingRequests.set(requestKey, promise)
  return promise
}

// API methods with proper typing
export const apiService = {
  // Authentication
  auth: {
    login: (credentials: { email: string; password: string }) =>
      api.post('/auth/login', credentials),

    register: (data: any) =>
      api.post('/auth/register', data),

    logout: () =>
      api.post('/auth/logout'),

    refresh: () =>
      api.post('/auth/refresh'),

    forgotPassword: (data: { email: string }) =>
      api.post('/auth/forgot-password', data),

    resetPassword: (data: { token: string; new_password: string }) =>
      api.post('/auth/reset-password', data),

    verifyEmail: (token: string, userId?: string) => {
      const params = new URLSearchParams({ token })
      if (userId) params.set('user_id', userId)
      return api.post(`/auth/verify-email?${params.toString()}`)
    },

    resendVerification: (data: { email: string }) =>
      api.post('/auth/resend-verification', data),
  },

  // Homepage Images
  homepage: {
    getImages: () =>
      api.get('/homepage/images'),
  },

  // Admin
  admin: {
    // Admin Authentication
    login: (credentials: any) =>
      api.post('/admin/auth/login', credentials),
    
    logout: () =>
      api.post('/admin/auth/logout'),
    
    refresh: (refreshToken: string) =>
      api.post('/admin/auth/refresh', { refresh_token: refreshToken }),
    
    me: () =>
      api.get('/admin/auth/me'),
    
    changePassword: (data: any) =>
      api.put('/admin/auth/change-password', data),
    
    // Admin Profile
    updateProfile: (data: any) =>
      api.put('/admin/profile', data),
    
    // Admin Management
    getAdmins: (params?: any) =>
      api.get('/admin/admins', { params }),
    
    createAdmin: (data: any) =>
      api.post('/admin/admins', data),
    
    updateAdmin: (adminId: string, data: any) =>
      api.put(`/admin/admins/${adminId}`, data),
    
    deleteAdmin: (adminId: string) =>
      api.delete(`/admin/admins/${adminId}`),
    
    // Admin Activity Logs
    getActivityLogs: (params?: any) =>
      api.get('/admin/activity-logs', { params }),
    
    logActivity: (data: any) =>
      api.post('/admin/activity-logs', data),
    
    getLoginLogs: (params?: any) =>
      api.get('/admin/login-logs', { params }),
    
    // Dashboard & Statistics
    getDashboardStats: () =>
      api.get('/admin/dashboard/stats'),
    
    getSystemStatus: () =>
      api.get('/admin/system/status'),
    
    // User Management
    getUsers: (params?: any) =>
      api.get('/admin/users', { params }),
    
    getUserDetail: (userId: string) =>
      api.get(`/admin/users/${userId}`),
    
    updateUser: (userId: string, data: any) =>
      api.put(`/admin/users/${userId}`, data),
    
    suspendUser: (userId: string, reason: string) =>
      api.post(`/admin/users/${userId}/suspend`, { reason }),
    
    unsuspendUser: (userId: string) =>
      api.post(`/admin/users/${userId}/unsuspend`),
    
    // Content Management
    getPendingContent: (params?: any) =>
      api.get('/admin/content/pending', { params }),
    
    approveContent: (contentId: string) =>
      api.post(`/admin/content/${contentId}/approve`),
    
    rejectContent: (contentId: string, reason: string) =>
      api.post(`/admin/content/${contentId}/reject`, { reason }),
    
    // Homepage Images Management
    getHomepageImages: () =>
      api.get('/admin/homepage/images'),
    
    uploadHomepageImage: (formData: FormData, config?: any) =>
      api.post('/admin/homepage/images/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        ...config
      }),
    
    updateHomepageImageStatus: (imageType: string, enabled: boolean) =>
      api.put(`/admin/homepage/images/${imageType}/status`, { enabled }),
    
    updateHomepageImageSettings: (settings: Array<{ type: string; enabled: boolean }>) =>
      api.put('/admin/homepage/images/settings', { settings }),
    
    deleteHomepageImage: (imageType: string) =>
      api.delete(`/admin/homepage/images/${imageType}`),
    
    // Finance Management
    getPayments: (params?: any) =>
      api.get('/admin/finance/payments', { params }),
    
    getSubscriptions: (params?: any) =>
      api.get('/admin/finance/subscriptions', { params }),
    
    getRevenue: (params?: any) =>
      api.get('/admin/finance/revenue', { params }),
    
    // Support Management
    getTickets: (params?: any) =>
      api.get('/admin/support/tickets', { params }),
    
    updateTicket: (ticketId: string, data: any) =>
      api.put(`/admin/support/tickets/${ticketId}`, data),
    
    // System Settings
    getSettings: () =>
      api.get('/admin/settings'),
    
    updateSettings: (data: any) =>
      api.put('/admin/settings', data),
  },

  // User management
  users: {
    getProfile: () =>
      deduplicatedGet('/users/profile'),

    updateProfile: (data: any) =>
      api.put('/users/profile', data),
    
    uploadPhoto: (formData: FormData) =>
      api.post('/users/upload-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }),
    
    // General file upload for chat and other purposes
    uploadFile: (formData: FormData, onUploadProgress?: (progressEvent: any) => void) =>
      api.post('/users/upload-file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      }),
    
    getPhotos: () =>
      deduplicatedGet('/users/photos'),
    
    updatePhoto: (photoId: string, data: any) =>
      api.put(`/users/photos/${photoId}`, data),
    
    deletePhoto: (photoId: string) =>
      api.delete(`/users/photos/${photoId}`),
    
    setCoverPhoto: (photoId: string) =>
      api.post(`/users/photos/${photoId}/cover`),
    
    updatePhotoPrivacy: (photoId: string, isPrivate: boolean) =>
      api.put(`/users/photos/${photoId}/privacy`, { is_private: isPrivate }),
    
    reorderPhotos: (photoIds: string[]) =>
      api.put('/users/photos/reorder', { photo_order: photoIds }),
    
    search: (params: any) =>
      api.get('/users/search', {
        params,
        // Gin binds slices from repeated keys (body_types=1&body_types=2).
        // Axios' default bracket form (body_types[]=1) does not match the Go
        // form tags used by SearchUsersRequest.
        paramsSerializer: { indexes: null }
      }),
    
    getRecommendations: (limit?: number) =>
      api.get('/users/recommendations', { params: { limit } }),
    
    getUserDetail: (userId: string) =>
      api.get(`/users/${userId}`),
    
    // 🚀 用戶互動API - 支持喜歡、收藏、封鎖等操作
    createInteraction: (userId: string, data: { interaction_type: number }) =>
      api.post(`/users/${userId}/interact`, data),

    removeInteraction: (userId: string, params: { type: number }) =>
      api.delete(`/users/${userId}/interact`, { params }),
    
    getInteractions: (params?: any) =>
      api.get('/users/interactions', { params }),
  },
  
  // Messaging
  messages: {
    send: (data: import('@/types/messages').SendMessageRequest) =>
      api.post('/messages/', data),
    
    getConversations: (params?: any) =>
      api.get('/messages/conversations', { params }),
    
    getConversation: (userId: string, params?: any) =>
      api.get(`/messages/${userId}`, { params }),
    
    markAsRead: (messageId: string) =>
      api.put(`/messages/${messageId}/read`),

    // 🚀 批量標記對話為已讀 - 高效的做法！一次API調用解決問題
    markConversationAsRead: (userId: string) =>
      api.put(`/messages/conversation/${userId}/read`),
    
    deleteConversation: (userId: string) =>
      api.delete(`/messages/${userId}`),
    
    search: (query: string) =>
      api.get('/messages/search', { params: { q: query } }),
    
    getStats: () =>
      api.get('/messages/stats'),
    
    getUnreadCount: () =>
      api.get('/messages/unread-count'),
    
    checkAccess: () =>
      api.get('/messages/access'),
  },
  
  // Subscriptions & Payments
  subscriptions: {
    getPlans: () =>
      api.get('/subscriptions/plans'),
    
    getFeatures: () =>
      api.get('/subscriptions/features'),
    
    create: (data: { 
      plan_id: string; 
      payment_method?: string;
      promo_code?: string;
      auto_renew?: boolean;
    }) =>
      api.post('/subscriptions/create', data),
    
    getStatus: () =>
      api.get('/subscriptions/status'),
    
    getCurrent: () =>
      api.get('/subscriptions/current'),
    
    getHistory: (page = 1, limit = 20) =>
      api.get('/subscriptions/history', { params: { page, limit } }),
    
    cancel: (reason?: string) =>
      api.post('/subscriptions/cancel', { reason }),
    
    validatePromo: (code: string, planId?: string) =>
      api.get('/subscriptions/validate-promo', { 
        params: { code, ...(planId && { plan_id: planId }) } 
      }),
    
    getStats: () =>
      api.get('/subscriptions/stats'),
  },

  // Payments
  payments: {
    createPaymentIntent: (data: {
      amount: number;
      currency: string;
      payment_method?: string;
      metadata?: Record<string, string>;
    }) =>
      api.post('/payments/create-intent', data),
    
    confirmPayment: (paymentIntentId: string, data?: {
      payment_method?: string;
      return_url?: string;
    }) =>
      api.post(`/payments/${paymentIntentId}/confirm`, data),
    
    getPaymentStatus: (paymentId: string) =>
      api.get(`/payments/${paymentId}/status`),
    
    // 注意：不支持退款功能
    // processRefund: (paymentId: string, amount?: number) =>
    //   api.post(`/payments/${paymentId}/refund`, { amount }),
    
    getPaymentMethods: () =>
      api.get('/payments/methods'),
    
    savePaymentMethod: (data: {
      type: string;
      card?: {
        number: string;
        exp_month: number;
        exp_year: number;
        cvc: string;
      };
    }) =>
      api.post('/payments/methods', data),
    
    deletePaymentMethod: (methodId: string) =>
      api.delete(`/payments/methods/${methodId}`),

    // PayPal specific methods
    createPayPalOrder: (data: {
      amount: number;
      currency: string;
      description?: string;
      return_url: string;
      cancel_url: string;
    }) =>
      api.post('/payments/paypal/orders', data),

    capturePayPalOrder: (orderId: string) =>
      api.post(`/payments/paypal/orders/${orderId}/capture`),

    webhook: (data: {
      payment_id: string;
      status: string;
      signature: string;
    }) =>
      api.post('/subscriptions/webhook/payment', data),
  },
  
  // Languages
  languages: {
    getSupportedLanguages: () =>
      api.get('/languages/'),
    
    getLanguagesForRegion: (region: string) =>
      api.get(`/languages/regions/${region}`),
    
    getCurrentLanguageInfo: () =>
      api.get('/languages/current'),
    
    getTranslations: (lang?: string) =>
      api.get('/languages/translations', { params: lang ? { lang } : {} }),
    
    setPreference: (language: string) =>
      api.post('/languages/preference', { language }),
    
    getPreference: () =>
      api.get('/languages/preference'),
  },
  
  // WebSocket
  websocket: {
    createTicket: () =>
      api.post('/ws/ticket'),

    getStats: () =>
      api.get('/ws/stats'),
    
    getOnlineUsers: () =>
      api.get('/ws/online-users'),
    
    checkUserStatus: (userId: string) =>
      api.get(`/ws/users/${userId}/status`),
  },
  
  // User Discovery (alias for users methods)
  discover: {
    searchUsers: (params: any) =>
      api.get('/users/search', { params }),
    
    getRecommendations: (limit?: number) =>
      api.get('/users/recommendations', { params: { limit } }),
    
    getUserDetail: (userId: string) =>
      api.get(`/users/${userId}`),
    
    createInteraction: (userId: string, data: { interaction_type: number }) =>
      api.post(`/users/${userId}/interact`, data),
    
    removeInteraction: (userId: string) =>
      api.delete(`/users/${userId}/interact`),
    
    getInteractions: (params?: any) =>
      api.get('/users/interactions', { params }),
  },

  // Generic methods
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    api.get(url, config),
  
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    api.post(url, data, config),
  
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    api.put(url, data, config),
  
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    api.delete(url, config),
  
  // Auth helpers
  setAuthHeader,
  clearAuthHeader,
}

export default api
